/**
 * ProfessionPrompt — "Dicci chi sei": scelta della professione dentro il catalogo
 *
 * Tre modalita' (decise dal server, vedi CatalogController::buildProfessionPrompt):
 * 'select' = "Seleziona la tua professione" (elenco; in cima dopo 1 s e ripetuto ogni 9 card
 * allo scorrimento; chiuderne uno collassa tutti, 30 giorni in localStorage);
 * 'ask' = "Sei un X?" con Si (cookie + ricarica), No (apre l'elenco), Ignora (fino al ricaricamento);
 * 'known' = "Professione: X" collassato, con l'elenco per cambiare (solo da cookie) e
 * "Tutte le professioni" che toglie il filtro.
 * La variante 'empty' (stato "Nessun corso trovato" con la professione nota come filtro) usa
 * solo forget(true): toglie la professione e ripete la ricerca per tutte le professioni.
 * La professione scelta finisce nel cookie `ik_profession` (un anno): e' il dato chiave per
 * personalizzare catalogo e comunicazioni anche prima della registrazione.
 * Su mobile l'elenco mostra 8 professioni e un "Tutte le professioni" che apre il resto.
 *
 * Uso:
 *   <section x-data="professionPrompt('top')" x-show="visible"> ... @click="close()"
 *   <section x-data="professionPrompt('inline')"> header @click="open = !open" ... body x-show="open"
 */
import { track, relay, generateEventId } from '../tracking/tracker.js';
import { saveProfession, clearProfession } from '../utils/profession-cookie.js';

const STORAGE_KEY = 'ik_prof_prompt_closed';
const TTL_MS = 30 * 24 * 60 * 60 * 1000;

function readClosed() {
    try {
        const ts = parseInt(localStorage.getItem(STORAGE_KEY) || '0', 10);
        return ts > 0 && (Date.now() - ts) < TTL_MS;
    } catch (e) {
        return false;
    }
}

export function registerProfessionPrompt(Alpine) {
    Alpine.store('profPrompt', {
        closed: readClosed(),
        close() {
            this.closed = true;
            try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch (e) { /* privato/bloccato */ }
        },
        reopen() {
            this.closed = false;
            try { localStorage.removeItem(STORAGE_KEY); } catch (e) { /* privato/bloccato */ }
        },
    });

    Alpine.data('professionPrompt', (variant = 'top', options = {}) => ({
        variant,
        mode: options.mode || 'select',          // 'select' | 'ask' | 'known'
        source: options.source || null,          // 'profile' | 'cookie' | null
        ask: options.ask || null,                // { id, slug, label } nella modalita' 'ask'
        labels: options.labels || {},
        disciplines: options.disciplines || {},
        expanded: false,
        open: true,
        // Il box compare con movimento (la griglia si abbassa via x-collapse, il box entra da
        // destra via CSS): in cima dopo un secondo, piu' in basso quando lo scorrimento lo raggiunge.
        revealed: false,
        view: 'occupations',
        current: { slug: '', id: null, label: '', url: '', disciplines: [] },
        fading: false,

        init() {
            // Aperto solo quando chiede di scegliere e l'utente non l'ha chiuso;
            // "Professione: X" parte collassato, "Sei un X?" non ha corpo finche' non si risponde No
            this.open = this.mode === 'select' && !this.$store.profPrompt.closed;
            if (this.variant === 'top') {
                setTimeout(() => this.reveal(), 1000);
            }
            // Chiuso un box → anche l'altro si collassa (e viceversa riaperto)
            this.$watch('$store.profPrompt.closed', (closed) => {
                if (this.mode === 'select') this.open = !closed;
            });
        },

        reveal() {
            this.revealed = true;
        },

        /** Linguetta: apre/chiude l'accordion; nella modalita' "scegli" la scelta vale per tutti i box e resta nel browser */
        toggle() {
            this.open = !this.open;
            if (this.mode !== 'select') return;
            if (this.open) {
                this.$store.profPrompt.reopen();
            } else {
                this.$store.profPrompt.close();
                track('close_profession_prompt', { variant: this.variant });
            }
        },

        /** "Sei un X?" → Si: la professione e' quella della pagina, la ricordiamo e ricarichiamo */
        confirmAsk() {
            if (!this.ask) return;
            saveProfession(this.ask.slug, this.ask.id, null);
            this.sendChoice(this.ask.slug, this.ask.id, null, 'confirm');
            window.location.reload();
        },

        /** "Sei un X?" → No: e' un'altra professione, apriamo l'elenco */
        declineAsk() {
            this.mode = 'select';
            this.open = true;
            track('decline_profession_prompt', { occupation: this.ask?.slug, variant: this.variant });
        },

        /** "Sei un X?" → Ignora: sparisce fino al prossimo caricamento della pagina */
        ignore() {
            this.revealed = false;
            track('ignore_profession_prompt', { occupation: this.ask?.slug, variant: this.variant });
        },

        /** "Tutte le professioni": via il filtro (cookie cancellato, o "all" per chi e' loggato).
         *  Dallo stato vuoto (variante 'empty') la ricerca e i filtri in corso restano: l'utente
         *  vuole lo stesso risultato, ma per tutte le professioni. */
        forget(keepQuery = false) {
            clearProfession(this.source === 'profile');
            track('forget_profession', { variant: this.variant });
            window.location.href = this.catalogBase() + (keepQuery ? window.location.search : '');
        },

        catalogBase() {
            const m = window.location.pathname.match(/^\/corsi-ecm\/(fad|residenziali)\//);
            return '/corsi-ecm/' + (m ? m[1] + '/' : '');
        },

        /** Cambio vista con dissolvenza: fade out, scambio, fade in */
        swap(view) {
            this.fading = true;
            setTimeout(() => {
                this.view = view;
                this.fading = false;
            }, 200);
        },

        showDisciplines(slug) {
            const occ = this.disciplines[slug];
            if (!occ) return;
            this.current = { slug, id: occ.id, label: occ.label, url: occ.url, disciplines: occ.disciplines || [] };
            this.swap('disciplines');
        },

        showOccupations() {
            this.swap('occupations');
        },

        /** Scelta esplicita dall'elenco: cookie prima di navigare */
        choose(slug, occupationId = null, disciplineId = null) {
            saveProfession(slug, occupationId, disciplineId);
            this.sendChoice(slug, occupationId, disciplineId, 'list');
        },

        sendChoice(slug, occupationId, disciplineId, how) {
            const eventId = generateEventId('select_profession');
            // GTM/GA4 (dataLayer) + registro locale
            track('select_profession', { event_id: eventId, occupation: slug, occupation_id: occupationId, discipline: disciplineId, variant: this.variant, how });
            // Meta: evento personalizzato, utile per pubblici per professione
            relay('SelectProfession', { event_id: eventId, content_name: slug, content_category: 'profession', content_ids: [String(occupationId || slug)] });
        },
    }));
}
